import { createContext, useCallback, useContext, useMemo, useState } from "react";

import {
    RaydiumPoolData,
    RaydiumPositionData,
    useAbfFetches,
    useRaydiumOffchainQuery,
    useUserTokens
} from "@bridgesplit/abf-react";
import { Slippage, SlippageValue } from "app/components/common";
import { RaydiumPositionExpanded, TokenPositionExpanded } from "@bridgesplit/abf-sdk";
import { lamportsToUiAmount } from "@bridgesplit/utils";
import { useLocalStorage } from "@bridgesplit/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import BN from "bn.js";

import {
    AddLiquidityQuote,
    DecreaseLiquidityQuote,
    RaydiumContextType,
    RaydiumContextValue,
    RaydiumDepositForm,
    OpenPositionForm,
    WithdrawForm,
    OpenPositionQuote,
    RefetchLutsFunction
} from "./types";
import { getPoolData, getPositionData } from "./raydium";
import { ActionType } from "../clmm/ManagePositionWrapper";

export const RaydiumContext = createContext<RaydiumContextType | null>(null);

export function useRaydiumContext() {
    const context = useContext(RaydiumContext);
    if (!context) {
        throw new Error("useRaydiumContext must be used within a RaydiumContext");
    }
    return context;
}

const RAYDIUM_SLIPPAGE_KEY = "raydium_slippage";
export const RAYDIUM_DEFAULT_SLIPPAGE_UI = 2.5;
export const AUTO_SLIPPAGE_DECIMALS = 0.1; // high slippage for auto-slippage

export function RaydiumProvider({ children, value }: { children: React.ReactNode; value: RaydiumContextValue }) {
    const [positionData, setPositionData] = useState<RaydiumPositionData | null | undefined>(undefined);
    const [initialWithdrawQuote, setInitialWithdrawQuote] = useState<DecreaseLiquidityQuote | undefined>(undefined);
    const [poolData, setPoolData] = useState<RaydiumPoolData | null | undefined>(undefined);
    const [depositForm, setDepositForm] = useState<RaydiumDepositForm>({});
    const [addLiquidityQuote, setAddLiquidityQuote] = useState<AddLiquidityQuote | undefined>(undefined);

    const [withdrawForm, setWithdrawForm] = useState<WithdrawForm>({});
    const [withdrawQuote, setWithdrawQuote] = useState<DecreaseLiquidityQuote | undefined>(undefined);

    const [openPositionForm, setOpenPositionForm] = useState<OpenPositionForm>({});
    const [openPositionQuote, setOpenPositionQuote] = useState<OpenPositionQuote | undefined>(undefined);

    const [isFlippedPrice, setIsFlippedPrice] = useState(false);
    const [isActionTypeInitialized, setIsActionTypeInitialized] = useState<ActionType[] | undefined>(undefined);
    const [refetchLuts, setRefetchLuts] = useState<RefetchLutsFunction | null>(null);

    const { resetRaydiumExternalApi, resetClmmPositions, resetLoanLuts } = useAbfFetches();
    const { refresh } = useUserTokens();

    const [cachedSlippage, setCachedSlippage] = useLocalStorage<SlippageValue>(
        RAYDIUM_SLIPPAGE_KEY,
        RAYDIUM_DEFAULT_SLIPPAGE_UI
    );

    // get the fresh raydium position by using up to date withdraw quote
    const raydiumPosition = useMemo((): RaydiumPositionExpanded => {
        const originalPosition = value.raydiumPosition;
        if (!initialWithdrawQuote) return originalPosition;

        const updateTokenBalance = (token: TokenPositionExpanded, amount: BN): TokenPositionExpanded => {
            const uiAmount = lamportsToUiAmount(amount.toNumber(), token.decimals);
            return { ...token, amount: uiAmount, usdAmount: token.usdPrice * uiAmount };
        };

        const tokenA = updateTokenBalance(originalPosition.tokenA, initialWithdrawQuote.estTokenA);
        const tokenB = updateTokenBalance(originalPosition.tokenB, initialWithdrawQuote.estTokenB);

        return {
            ...originalPosition,
            totalPrice: tokenA.usdAmount + tokenB.usdAmount,
            tokenA,
            tokenB
        };
    }, [value.raydiumPosition, initialWithdrawQuote]);

    const { data: raydiumOffchainRawResponse, isLoading: isRaydiumOffchainQueryLoading } = useRaydiumOffchainQuery(
        value.raydiumPosition.poolAddress ? { ids: [value.raydiumPosition.poolAddress] } : skipToken
    );

    // While loading, raydiumOffchain is undefined. When data is available, try to find the pool.
    const raydiumOffchain = useMemo(() => {
        const poolAddress = value.raydiumPosition.poolAddress;
        if (!raydiumOffchainRawResponse || isRaydiumOffchainQueryLoading) return undefined;

        const raydiumOffchainPools = raydiumOffchainRawResponse.data;
        if (!raydiumOffchainPools.length) return undefined;

        for (const raydiumPool of raydiumOffchainPools) {
            if (raydiumPool && raydiumPool.id === poolAddress) return raydiumPool;
        }

        return undefined;
    }, [value.raydiumPosition.poolAddress, raydiumOffchainRawResponse, isRaydiumOffchainQueryLoading]);

    const resetForms = useCallback(() => {
        setOpenPositionForm((prev) => ({
            ...prev,
            side: prev.side ?? "A",
            status: "silent-refetch"
        }));
        setWithdrawForm((prev) => ({
            ...prev,
            status: "silent-refetch"
        }));
        setDepositForm((prev) => ({
            ...prev,
            side: prev.side ?? "A",
            status: "silent-refetch"
        }));
    }, []);

    const slippageController = Slippage.useController({
        presetsUi: [1, 2.5, 5],
        defaultSlippageUi: cachedSlippage ?? RAYDIUM_DEFAULT_SLIPPAGE_UI,
        onEdit: (value) => {
            if (value !== undefined) {
                setCachedSlippage(value);
            }
        },
        showAuto: true
    });

    const forceFetchRaydiumData = useCallback(async () => {
        const [poolData, positionData] = await Promise.all([
            getPoolData(raydiumPosition.poolAddress, value.loanExpanded.loan.address),
            getPositionData(raydiumPosition, value.loanExpanded.loan.address)
        ]);
        setPoolData(poolData);
        setPositionData(positionData?.positionData ?? null);
        setInitialWithdrawQuote(positionData?.quote);
    }, [raydiumPosition, value.loanExpanded.loan.address]);

    const reset = useCallback(async () => {
        setIsActionTypeInitialized(undefined);
        resetLoanLuts();
        if (refetchLuts) {
            await refetchLuts();
        }
        await forceFetchRaydiumData();
        resetForms();
        resetRaydiumExternalApi();
        void refresh();
        resetClmmPositions();
    }, [
        forceFetchRaydiumData,
        resetForms,
        resetLoanLuts,
        resetRaydiumExternalApi,
        resetClmmPositions,
        refresh,
        refetchLuts
    ]);

    const poll = useCallback(async () => {
        resetForms();
        void refresh();
        await forceFetchRaydiumData();
    }, [resetForms, forceFetchRaydiumData, refresh]);

    const contextValue: RaydiumContextType = {
        ...value,

        raydiumPosition,
        raydiumOffchain,
        positionData,
        setPositionData,

        poolData,
        setPoolData,

        initialWithdrawQuote,
        setInitialWithdrawQuote,

        depositForm,
        setDepositForm,

        addLiquidityQuote,
        setAddLiquidityQuote,

        withdrawForm,
        setWithdrawForm,
        withdrawQuote,
        setWithdrawQuote,

        openPositionForm,
        setOpenPositionForm,
        openPositionQuote,
        setOpenPositionQuote,

        isFlippedPrice,
        setIsFlippedPrice,
        isActionTypeInitialized,
        setIsActionTypeInitialized,
        refetchLuts,
        setRefetchLuts,
        slippageController,

        reset,
        poll
    };

    return <RaydiumContext.Provider value={contextValue}>{children}</RaydiumContext.Provider>;
}
